// Copyright (c) 2023 WiseTime. All rights reserved.

import { CreateEmptyTimeGroupRequest, TimeGroup } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { concat, EMPTY, forkJoin, Observable, of } from "rxjs"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { showSnackBar } from "../../snackbar/actions"
import { t } from "@lingui/macro"
import * as TimelineActions from "../actions"
import { Action } from "redux"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import * as TimelineSlice from "../slices/timeline"
import * as UserContextSlice from "../../user-context/slice"
import { grpc } from "../../../app/rpc/executor"
import { timelineClient, timesheetFieldClient } from "../../../app/rpc/client"
import * as Actions from "../actions"
import { RootState } from "../../../app/store"
import {
  BatchGetTimesheetFieldConfigsRequest,
  GenerateTimesheetFieldConfigsRequest,
  GenerateTimesheetFieldConfigsResponse,
  TimesheetFieldConfigRef,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

export const getLastActiveCell = (timeGroup: TimeGroup.AsObject): TimelineSlice.Cell => {
  const hasTags = timeGroup.tagsList.length !== 0
  const hasDuration = timeGroup.totalDuration !== undefined
    && (timeGroup.totalDuration.seconds > 0 || timeGroup.totalDuration.nanos > 0)
  const noData = !hasTags
    && !hasDuration
    && timeGroup.narrative.length === 0
  if (noData) {
    return TimelineSlice.Cell.TIME_ROWS
  }
  if (hasTags) {
    return TimelineSlice.Cell.TAG
  }
  if (hasDuration) {
    return TimelineSlice.Cell.DURATION
  }
  return TimelineSlice.Cell.NARRATIVE
}

type CreateEmptyTimeGroupRpc = (request: CreateEmptyTimeGroupRequest) => Observable<TimeGroup>
type GenerateTimesheetFieldConfigsRpc = (request: GenerateTimesheetFieldConfigsRequest) => Observable<GenerateTimesheetFieldConfigsResponse>

export const createEmptyTimeGroup = (
  createEmptyTimeGroupRpc: CreateEmptyTimeGroupRpc,
  generateTimesheetFieldConfigsRpc: GenerateTimesheetFieldConfigsRpc,
) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(TimelineActions.createEmptyTimeGroup.match),
      withLatestFrom(state$),
      mergeMap(([{ payload }, state]) => {
        const currentSiteId = UserContextSlice.selectCurrentSiteId(state)
        const timesheetFieldConfigsCache = TimelineSlice.selectTimesheetFieldConfigsCache(state)
        const pendingTimesheetFieldConfigRequests = TimelineSlice.selectPendingTimesheetFieldConfigRequests(state)
        const tagId = payload.request.toObject().tagsList
          .filter(t => t.siteId === currentSiteId)?.[0]?.id ?? ""
        const generateTimesheetFieldConfigs = currentSiteId ? generateTimesheetFieldConfigsRpc(
          new GenerateTimesheetFieldConfigsRequest().setSiteId(currentSiteId).setTagId(tagId)
        ).pipe(
          catchError(() => of(undefined))
        ) : of(undefined)
        return forkJoin([
          createEmptyTimeGroupRpc(payload.request),
          generateTimesheetFieldConfigs,
        ])
          .pipe(
            mergeMap(([timeGroupFromResponse, generateTimesheetFieldConfigsResponse]) => {
              const timeGroup = timeGroupFromResponse.toObject()
              const configRefsList = generateTimesheetFieldConfigsResponse?.toObject().configRefsList ?? []
              const nonCachedConfigRefsList = configRefsList
                .filter(configRef => (
                  timesheetFieldConfigsCache[configRef.configHash] === undefined
                  && !pendingTimesheetFieldConfigRequests.includes(configRef.configHash)
                ))
              const {
                onSuccess,
                createTimeGroupRowIndex,
                skipSelection,
              } = payload
              onSuccess && onSuccess()
              const isCreatedFromEmptyRow = createTimeGroupRowIndex !== undefined
              const optionalRemoveFromCreateRowIndexesList = isCreatedFromEmptyRow ?
                of(TimelineSlice.removeFromCreateRowIndexesList(createTimeGroupRowIndex)) : EMPTY
              const optionalSetTimeGroupFieldConfigs = configRefsList.length !== 0 ?
                of(TimelineSlice.setTimeGroupFieldConfigs({
                  timeGroupId: timeGroup.id,
                  configHashList: configRefsList.map(configRef => configRef.configHash),
                })) : EMPTY
              const optionalBatchGetTimesheetFieldConfigs = nonCachedConfigRefsList.length && currentSiteId ? of(
                Actions.batchGetTimesheetFieldConfigs(
                  new BatchGetTimesheetFieldConfigsRequest()
                    .setSiteId(currentSiteId)
                    .setConfigRefsList(
                      nonCachedConfigRefsList.map(configRef => (
                        new TimesheetFieldConfigRef()
                          .setTimesheetFieldId(configRef.timesheetFieldId)
                          .setConfigHash(configRef.configHash)
                      ))
                    )
                )
              ) : EMPTY
              return concat(
                of(TimelineSlice.upsertTimeGroup(timeGroup)),
                of(TimelineSlice.addTimeGroupAwaitingSuggestions(timeGroup.id)),
                optionalBatchGetTimesheetFieldConfigs,
                optionalSetTimeGroupFieldConfigs,
                optionalRemoveFromCreateRowIndexesList,
                of(TimelineSlice.setKeepTabOrder({
                  timeGroupId: timeGroup.id,
                  cell: getLastActiveCell(timeGroup),
                })),
                of(TimelineSlice.replaceActiveTimeGroupsIds({
                  timeGroupsIds: skipSelection ? [] : [timeGroup.id],
                })),
              )
            }),
            catchError((e) => handleError(e, payload)),
          )
      })
    )

const handleError = (e: unknown, payload: TimelineActions.CreateEmptyTimeGroupPayload) => {
  payload.onError && payload.onError()
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_create_empty_group_permission_denied", message: "Failed to create time group. You do not have permission to perform this action." }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_create_time_group_failed", message: "Failed to create the group. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const createEmptyTimeGroupRpc = (request: CreateEmptyTimeGroupRequest): Observable<TimeGroup> =>
  grpc(() => timelineClient.createEmptyTimeGroup(request, null))
const generateTimesheetFieldConfigsRpc = (request: GenerateTimesheetFieldConfigsRequest): Observable<GenerateTimesheetFieldConfigsResponse> =>
  grpc(() => timesheetFieldClient.generateTimesheetFieldConfigs(request, null))

export default createEmptyTimeGroup(createEmptyTimeGroupRpc, generateTimesheetFieldConfigsRpc)
